// ---------------------------------------------------------------------------
// C3+C5: Local SEO Dashboard + Recommendations
// ---------------------------------------------------------------------------
// Per-location SEO scores with breakdown. AI recommendations with actions.
// Design system applied from line 1.
// ---------------------------------------------------------------------------
import { useState } from "react";
import {
  Search,
  MapPin,
  Star,
  Globe,
  FileText,
  Camera,
  AlertTriangle,
  Sparkles,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from "lucide-react";
import { useApi } from "@/hooks/use-api";
import { EmptyState } from "@/components/ui/empty-state";
import { formatDate } from "@/lib/i18n";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SeoBreakdown {
  keywords: { score: number; tracked: number; inTop3: number; inTop10: number };
  citations: { score: number; total: number; consistent: number; inconsistent: number };
  reviews: { score: number; count: number; averageRating: number; responseRate: number };
  nap: { score: number; accurate: boolean; issues: string[] };
  photos: { score: number; count: number; recentCount: number };
}

interface LocationSeo {
  locationId: string;
  locationName: string;
  overallScore: number;
  breakdown: SeoBreakdown;
  lastUpdated: string;
}

interface SeoRecommendation {
  id: string;
  locationId: string;
  category: string;
  priority: "high" | "medium" | "low";
  title: string;
  description: string;
  impact: string;
  actionLabel: string;
  actionType: string;
  actionTarget?: string;
}

interface LocalSeoData {
  locations: LocationSeo[];
  recommendations: SeoRecommendation[];
  coverage?: Array<{
    locationId: string;
    reviews: "live";
    citations: "not_configured" | "live";
    keywords: "not_configured" | "live";
    nap: "partial" | "live";
    photos: "not_configured" | "live";
  }>;
}

// ---------------------------------------------------------------------------
// Score Ring
// ---------------------------------------------------------------------------

function ScoreRing({ score, size = 64 }: { score: number; size?: number }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? "var(--color-success)" : score >= 50 ? "var(--color-warning)" : "var(--color-destructive)";

  return (
    <svg width={size} height={size} className="flex-shrink-0">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--color-border)" strokeWidth={4} />
      <circle
        cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke={color} strokeWidth={4} strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        className="transition-all duration-700"
      />
      <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central"
        className="text-lg font-bold fill-foreground">{score}</text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Category Icons
// ---------------------------------------------------------------------------

const CATEGORY_ICON: Record<string, typeof Search> = {
  keywords: Search,
  citations: Globe,
  reviews: Star,
  nap: FileText,
  photos: Camera,
  content: FileText,
};

const PRIORITY_COLORS = {
  high: "border-l-[var(--color-destructive)] bg-[var(--color-destructive)]/30",
  medium: "border-l-[var(--color-warning)] bg-[var(--color-warning)]/30",
  low: "border-l-[var(--color-primary)] bg-[var(--color-primary-light)]/30",
};

// ---------------------------------------------------------------------------
// Location Card
// ---------------------------------------------------------------------------

function LocationCard({ location, recommendations }: { location: LocationSeo; recommendations: SeoRecommendation[] }) {
  const [expanded, setExpanded] = useState(false);
  const b = location.breakdown;

  return (
    <div className="card-hover rounded-xl border border-border bg-background overflow-hidden">
      <div className="p-4">
        <div className="flex items-center gap-4">
          <ScoreRing score={location.overallScore} />
          <div className="flex-1">
            <h3 className="font-medium text-foreground">{location.locationName}</h3>
            <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" />
              Updated {formatDate(location.lastUpdated)}
            </div>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="rounded-lg border border-border p-1.5 hover:bg-muted transition-colors"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>

        {/* Score bars */}
        <div className="mt-4 grid grid-cols-5 gap-2">
          {[
            { label: "Keywords", score: b.keywords.score, detail: `${b.keywords.inTop10}/${b.keywords.tracked} in top 10` },
            { label: "Citations", score: b.citations.score, detail: `${b.citations.consistent}/${b.citations.total} consistent` },
            { label: "Reviews", score: b.reviews.score, detail: `${b.reviews.count} reviews (${b.reviews.averageRating.toFixed(1)})` },
            { label: "NAP", score: b.nap.score, detail: b.nap.accurate ? "Accurate" : `${b.nap.issues.length} issues` },
            { label: "Photos", score: b.photos.score, detail: `${b.photos.count} total (${b.photos.recentCount} recent)` },
          ].map((item) => (
            <div key={item.label} className="text-center">
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${item.score}%`,
                    backgroundColor: item.score >= 80 ? "var(--color-success)" : item.score >= 50 ? "var(--color-warning)" : "var(--color-destructive)",
                  }}
                />
              </div>
              <span className="text-[10px] text-muted-foreground mt-1 block">{item.label}</span>
              <span className="text-[10px] text-muted-foreground">{item.detail}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Expanded: Recommendations */}
      {expanded && recommendations.length > 0 && (
        <div className="border-t border-border px-4 py-3 space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <Sparkles className="h-3 w-3 text-[var(--color-ai)]" />
            AI Recommendations
          </h4>
          {recommendations.map((rec) => {
            const Icon = CATEGORY_ICON[rec.category] ?? AlertTriangle;
            return (
              <div key={rec.id} className={`rounded-lg border-l-4 p-3 ${PRIORITY_COLORS[rec.priority]}`}>
                <div className="flex items-start gap-2">
                  <Icon className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{rec.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{rec.description}</p>
                    <p className="mt-1 text-[10px] text-[var(--color-ai)]">{rec.impact}</p>
                  </div>
                  <button className="flex-shrink-0 rounded-lg bg-[var(--color-primary)] px-3 py-1 text-[10px] font-medium text-white hover:bg-[var(--color-primary)]/90 transition-colors">
                    {rec.actionLabel}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function LocalSeoDashboard() {
  const { data, isLoading, error } = useApi<LocalSeoData>("/crm/reviews/local-seo");

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-48 animate-pulse rounded-xl bg-muted" style={{ animationDelay: `${i * 50}ms` }} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
        <AlertCircle className="h-4 w-4" />
        Failed to load SEO data.
      </div>
    );
  }

  if (!data || data.locations.length === 0) {
    return (
      <EmptyState
        icon={<Search className="h-8 w-8" />}
        title="No locations configured"
        description="Add your business locations to track local SEO performance, keyword rankings, and get AI-powered improvement recommendations."
        actionLabel="Add Location"
        onAction={() => {}}
        variant="info"
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Local SEO</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Per-location SEO performance. Click a location to see AI recommendations.
        </p>
      </div>

      {data.coverage?.some((item) =>
        item.citations !== "live" ||
        item.keywords !== "live" ||
        item.photos !== "live" ||
        item.nap !== "live",
      ) && (
        <div className="flex items-start gap-2 rounded-lg border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/8 p-4 text-sm text-muted-foreground">
          <AlertTriangle className="mt-0.5 h-4 w-4 text-[var(--color-warning)]" />
          <p>
            Review and profile data are live. Citation tracking, keyword tracking, and richer photo coverage are still being
            mounted on this surface, so missing categories are shown as gaps rather than estimated values.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {data.locations.map((loc) => (
          <LocationCard
            key={loc.locationId}
            location={loc}
            recommendations={data.recommendations.filter((r) => r.locationId === loc.locationId)}
          />
        ))}
      </div>
    </div>
  );
}
